// See EnrollHostsDrawer.spec.md for EHD-FR-001, EHD-FR-002, EHD-FR-007, EHD-FR-008, EHD-FR-009
import Button from 'pf9-ui-components/built/elements/button/Button'
import { useCallback, useEffect, useRef, useState } from 'react'
import { canEnrollHosts } from 'app/plugins/infrastructure/components/common/service-health/storage-connectivity/helpers'
import Alert from 'core/components/Alert'
import GridDefaultActionButton, {
  type GridDefaultActionButtonProps,
} from 'core/elements/grid/buttons/GridDefaultActionButton'
import type { GridBatchActionSpec } from 'core/elements/grid/hooks/useGridSelectableRows'
import Modal from 'core/elements/modal/Modal'
import StepperModalForm, {
  type ModalStep,
  type StepperModalFormHandle,
} from 'core/elements/modal/StepperModalForm'
import Text from 'core/elements/Text'
import type { IResmgrHostSelector } from 'openstack/components/infrastructure/model'
import generateTestId from 'utils/test-helpers'
import { isNewHost } from '../helpers'
import EnrollmentHeader, { EnrollHostsLabel } from './EnrollmentHeader'
import { useEnrollmentRunnerContext } from './EnrollmentRunnerContext'
import EnrollmentVerification from './EnrollmentVerification'
import type { EnrollmentSelection } from './model'
import NetworkConfigStep, { validateNetworkStep } from './NetworkConfigStep'
import RolesStep from './RolesStep'
import SelectClusterStep from './SelectClusterStep'
import SummaryStep from './SummaryStep'
import VolumeBackendsStep from './VolumeBackendsStep'

// Button derives data-testid from its children, and the label below is a node (text plus the
// Beta badge), so pin the id the plain "Enroll Hosts" label would have produced.
function EnrollHostsActionButton<T>(props: GridDefaultActionButtonProps<T>) {
  return <GridDefaultActionButton {...props} data-testid={generateTestId('Enroll Hosts')} />
}

// EHD-FR-009: the Enroll Hosts batch action, shared by the hosts list and host details pages.
// Only offered when the enroll_hosts flag is on (default on) and the user can run the storage
// check, the same gate as the tray and runner (ENT-FR-001). Returns an array so callers can
// spread it into their actions.
export const getEnrollHostsBatchActions = <T extends { roles?: string[]; v1Roles?: string[] }>(
  hostConfigsCount: number,
  onOpen: (hosts: T[]) => void,
): GridBatchActionSpec<T>[] =>
  canEnrollHosts()
    ? [
        {
          cond: (hosts) => hosts.length > 0 && hostConfigsCount > 0 && hosts.every(isNewHost),
          disabledTooltip:
            hostConfigsCount === 0
              ? 'You must first create host configurations in the cluster blueprint before assigning any cluster roles.'
              : 'You can only enroll new hosts. Deselect hosts that already have roles.',
          label: <EnrollHostsLabel />,
          BatchActionButton: EnrollHostsActionButton,
          icon: 'server',
          handleAction: onOpen,
          refreshAfterSuccess: false,
        },
      ]
    : []

// Props every step component receives, so each step is built the same way without
// re-threading selection state through the drawer.
export interface EnrollStepProps {
  selection: EnrollmentSelection
  onChange: (patch: Partial<EnrollmentSelection>) => void
  hosts: IResmgrHostSelector[]
  errors: Record<string, string>
}

interface Props {
  hosts: IResmgrHostSelector[]
  onClose: () => void
  // EHD-FR-008: when set, the drawer shows the read only verification view for this run
  runId?: string
}

const defaultSelection: EnrollmentSelection = {
  hypervisor: false,
  clusterName: '',
  hostConfigId: '',
  hostConfigName: '',
  backends: [],
  imageLibrary: false,
  remoteSupport: false,
  dns: false,
}

const NOT_AVAILABLE = 'This enrollment is no longer available.'
// EHD-FR-001: says how to continue, not only what is missing
const CLUSTER_REQUIRED = 'Select a cluster, or clear Apply Hypervisor Role to continue without one.'
// A common wide drawer width in the app (used by 20 modals)
const DRAWER_MAX_WIDTH = 1000

function CloseFooter({ onClose }: { onClose: () => void }) {
  return (
    <Button variant="secondary" type="button" onClick={onClose}>
      Close
    </Button>
  )
}

// EHD-FR-008: the run's details from the tray. No stepper and nothing editable but Retry check.
function ReadOnlyEnrollment({ runId, onClose }: { runId: string; onClose: () => void }) {
  const { runs, loaded, retryCheck } = useEnrollmentRunnerContext()
  const run = runs.find((item) => item.id === runId)

  return (
    <Modal
      open
      panel="drawer"
      maxWidth={DRAWER_MAX_WIDTH}
      aria-label="Enroll Hosts"
      onClose={onClose}
      title={
        run ? (
          <EnrollmentHeader hostsCount={run.hosts.length} selection={run.selection} />
        ) : (
          'Enroll Hosts'
        )
      }
      footer={<CloseFooter onClose={onClose} />}
    >
      <>
        {run && (
          <EnrollmentVerification
            run={run}
            onRetry={(serviceHost) => retryCheck(runId, serviceHost)}
          />
        )}
        {/* Runs load from preferences, so only say "gone" once they have loaded */}
        {!run && loaded && <Text variant="body2">{NOT_AVAILABLE}</Text>}
      </>
    </Modal>
  )
}

function EnrollWizard({ hosts, onClose }: { hosts: IResmgrHostSelector[]; onClose: () => void }) {
  const { runs, startRun, retryCheck } = useEnrollmentRunnerContext()
  const stepRef = useRef<StepperModalFormHandle>(null)
  const mountedRef = useRef(true)
  const [selection, setSelection] = useState<EnrollmentSelection>(defaultSelection)
  const [errors, setErrors] = useState<Record<string, string>>({})
  const [submitting, setSubmitting] = useState(false)
  const [applyError, setApplyError] = useState('')
  const [runId, setRunId] = useState('')

  // Set back to true on every mount: fast refresh and StrictMode re-run effects but keep refs.
  useEffect(() => {
    mountedRef.current = true
    return () => {
      mountedRef.current = false
    }
  }, [])

  // Step 6 (index 5) is never reachable through stepper clicks before a run exists, since
  // visitedStep only grows through Next/goToStep navigation. This is the only way in.
  useEffect(() => {
    if (runId) stepRef.current?.goToStep(5)
  }, [runId])

  const handleChange = useCallback((patch: Partial<EnrollmentSelection>) => {
    setSelection((prev) => ({ ...prev, ...patch }))
  }, [])

  // EHD-FR-001: hypervisor ticked with no cluster blocks Next
  const validateClusterStep = useCallback(() => {
    if (selection.hypervisor && !selection.clusterName) {
      setErrors({ clusterName: CLUSTER_REQUIRED })
      return { success: false, error: { message: CLUSTER_REQUIRED } }
    }
    setErrors({})
    return { success: true }
  }, [selection])

  // EHD-FR-001: no host config picked blocks Next
  const validateNetwork = useCallback(() => {
    const stepErrors = validateNetworkStep(selection)
    setErrors(stepErrors)
    const message = stepErrors.hostConfigId
    return message ? { success: false, error: { message } } : { success: true }
  }, [selection])

  // EHD-FR-007: start the run with exactly what the summary shows (SC-002). On failure we stay
  // on step 5 with the message so the user can try again.
  const handleApply = useCallback(async () => {
    setSubmitting(true)
    setApplyError('')
    try {
      const id = await startRun(selection, hosts)
      if (mountedRef.current) setRunId(id)
    } catch (err) {
      if (mountedRef.current) setApplyError((err as Error)?.message || 'Unknown error')
    } finally {
      if (mountedRef.current) setSubmitting(false)
    }
  }, [startRun, selection, hosts])

  const stepProps: EnrollStepProps = { selection, onChange: handleChange, hosts, errors }
  const run = runs.find((item) => item.id === runId)

  const steps: ModalStep[] = [
    {
      stepId: 1,
      nextButtonText: 'Next: Network Configuration',
      component: <SelectClusterStep {...stepProps} />,
      stepValidator: validateClusterStep,
    },
    {
      stepId: 2,
      nextButtonText: 'Next: Volume Backends',
      component: <NetworkConfigStep {...stepProps} />,
      stepValidator: validateNetwork,
    },
    {
      stepId: 3,
      nextButtonText: 'Next: Roles',
      component: <VolumeBackendsStep {...stepProps} />,
    },
    {
      stepId: 4,
      nextButtonText: 'Next: Summary',
      component: <RolesStep {...stepProps} />,
    },
    {
      stepId: 5,
      // isSubmitStep: Figma shows all 6 step circles from the start, so step 6 (verification)
      // always exists even before a run does. Step 5 still submits via Apply & Verify though it
      // is no longer the stepper's last step.
      isSubmitStep: true,
      // The apply error is shown here instead of StepperModalForm's `error` prop: that error
      // sticks across goToStep, so a failed then successful apply would carry it onto step 6.
      component: (
        <>
          {applyError && <Alert variant="error" message={applyError} />}
          <SummaryStep {...stepProps} onEdit={(index) => stepRef.current?.goToStep(index)} />
        </>
      ),
    },
    {
      stepId: 6,
      // Only reachable via goToStep(5) after a successful apply below; the stepper's own click
      // handler never allows it earlier since visitedStep only grows through navigation and
      // never reaches index 5 until that jump happens.
      component: run ? (
        <EnrollmentVerification
          run={run}
          onRetry={(serviceHost) => retryCheck(runId, serviceHost)}
        />
      ) : null,
      footer: <CloseFooter onClose={onClose} />,
    },
  ]

  return (
    <StepperModalForm
      open
      panel="drawer"
      maxWidth={DRAWER_MAX_WIDTH}
      // Names the drawer for tests and screen readers: Modal spreads it onto its <article>
      aria-label="Enroll Hosts"
      title={<EnrollmentHeader hostsCount={hosts.length} selection={selection} />}
      onClose={onClose}
      onSubmit={handleApply}
      submitting={submitting}
      submitTitle="Apply & Verify"
      stepRef={stepRef}
      // A started run keeps going after close, so there is nothing to discard
      showConfirmationOnCancel={!runId}
      steps={steps}
      // Cancel and Next grouped on the right. Errors already show at the top and on the field.
      buttonAlignment="right"
      showFooterError={false}
      // After a failed Next, any change to the selection re-runs the step check so the errors
      // clear once fixed. `selection` only changes on edits, so this can't loop.
      params={selection as unknown as Record<string, unknown>}
    />
  )
}

export default function EnrollHostsDrawer({ hosts, onClose, runId }: Props) {
  // ENT-FR-005: hide the tray while this drawer is open so it can't cover the footer.
  const { setDrawerOpen } = useEnrollmentRunnerContext()
  useEffect(() => {
    setDrawerOpen(true)
    return () => setDrawerOpen(false)
  }, [setDrawerOpen])

  return runId ? (
    <ReadOnlyEnrollment runId={runId} onClose={onClose} />
  ) : (
    <EnrollWizard hosts={hosts} onClose={onClose} />
  )
}
